import { Widget } from '@/components/widget';
import { ObjectsTable } from '@/components/objects-table';
import { useUserDataclasses } from '@/hooks/user/use-user-dataclasses';
import { useEmitter } from '@/hooks/use-emitter';
import { useTabId } from '@/hooks/use-tab-id';
import { useScreenId } from '@/hooks/use-screen-id';
import { useSchemaDelete } from '@/hooks/use-schema-delete';
import { Modal } from '@/components/modal';
import { useState } from 'react';

interface SchemasListWidgetProps {
  id: string;
  hideable?: boolean;
}

export function SchemasListWidget({
  id,
  hideable = false,
}: SchemasListWidgetProps) {
  const { data, isLoading } = useUserDataclasses();
  const tabId = useTabId();
  const screenId = useScreenId();
  const emit = useEmitter({ id, listenersPrefix: `${tabId}/${screenId}` });
  const deleteDataClass = useSchemaDelete();
  const [pendingDeleteUuid, setPendingDeleteUuid] = useState<string | null>(null);

  function handleDragData(uuid: string, label?: string) {
    return JSON.stringify({
      type: 'object/insert',
      value: [{ uuid, type: 'schema', label }],
    });
  }

  function handleEdit(uuid: string) {
    emit({ type: 'DataClass', data: [{ uuid }] });
  }

  function handleDeleteConfirm() {
    if (!pendingDeleteUuid) return;
    deleteDataClass.mutate(pendingDeleteUuid, {
      onSuccess: () => setPendingDeleteUuid(null),
    });
  }

  const transformedSchemas = (data?.dataClasses ?? [])
    .filter((dc) => dc.schemaType === 'data' || dc.schemaType === 'vaultItem')
    .map((dc) => ({ uuid: dc.value, name: dc.label }));

  return (
    <Widget id={id} title="Schemas" hideable={hideable}>
      <ObjectsTable
        data={transformedSchemas}
        isLoading={isLoading}
        setDragData={handleDragData}
        onEdit={handleEdit}
        onDelete={(uuid) => setPendingDeleteUuid(uuid)}
      />
      {pendingDeleteUuid && (
        <Modal
          onCancel={() => setPendingDeleteUuid(null)}
          onSubmit={handleDeleteConfirm}
          loading={deleteDataClass.isLoading}
          title="Delete Schema"
          message="Are you sure you want to delete this schema? This action cannot be undone."
          actionName="Delete"
          offsetSidebar
        />
      )}
    </Widget>
  );
}
